<script setup lang="ts">
import DesktopNav from '@/components/nav/DesktopNav.vue'
import MobileNav from '@/components/nav/MobileNav.vue'
import { useViewportSize } from '@/components/composables/useViewportSize'

const { isMobile } = useViewportSize()

import { useI18n } from 'vue-i18n'
import { useSessionStore } from '@/stores/session'
import { useStore } from '@/stores'

const sessionStore = useSessionStore()
const store = useStore()
const i18n = useI18n()
import { onMounted, ref, watch } from 'vue'
import FullScreenLoading from '@/components/FullScreenLoading.vue'
import { useRouter, useRoute } from 'vue-router'
import Toast from 'primevue/toast'
import type { UserData } from '@/modules/api/get/userData.ts'
import { API } from '@/modules/api'

let userData: UserData | null = null
const router = useRouter()
const route = useRoute()
const initialLoginCheckProcessing = ref(true)
const isRouteTransitioning = ref(false)
const storeInitialized = ref(false)
const showSsoDialog = ref(false)

// Check if we're on a landing page that needs immediate rendering
const isLandingPage = () => {
  const path = window.location.pathname
  return path === '/landing-powerschool' || path.startsWith('/landing-lobby')
}

const isStandalonePage = () => {
  return window.location.pathname.startsWith('/standalone/')
}

// Public enrollment embed page (/enrollment/:token) — no login, no app shell.
// Note: the bare `/enrollment` (authenticated) page is intentionally excluded.
const isEnrollmentEmbedPage = () => {
  return /^\/enrollment\/.+/.test(window.location.pathname)
}

// Pages that render without the authenticated app shell (no nav, no auth bootstrap).
const isChromelessPage = () => {
  return isStandalonePage() || isEnrollmentEmbedPage()
}

// If we're on a landing page or a chromeless page, skip the initial loading
if (isLandingPage() || isChromelessPage()) {
  initialLoginCheckProcessing.value = false
}

router.beforeEach(() => {
  isRouteTransitioning.value = true
})

router.afterEach(() => {
  isRouteTransitioning.value = false
})

// Watch for store initialization
watch(
  () => store.isLoaded,
  isLoaded => {
    if (isLoaded) {
      storeInitialized.value = true
    }
  }
)

// Check if user came from SSO login and show dialog on any page
const checkAndShowSsoDialog = async () => {
  // Skip if on landing pages
  if (isLandingPage()) {
    return
  }

  // Check if store is loaded
  if (!store.isLoaded) {
    return
  }

  // Check sessionStorage for SSO login completion flag
  const ssoLoginCompleted = sessionStorage.getItem('sso_login_completed')
  const ssoLoginTimestamp = sessionStorage.getItem('sso_login_timestamp')

  if (ssoLoginCompleted !== 'true' || !ssoLoginTimestamp) {
    return
  }

  // Check if we've already shown the dialog for this SSO login
  const ssoDialogShownKey = `sso_dialog_shown_${ssoLoginTimestamp}`
  const hasShownDialog = sessionStorage.getItem(ssoDialogShownKey)

  if (hasShownDialog) {
    return
  }

  // Get registration activation status from the backend
  try {
    userData = await store.userData()
    const showDialog = userData.registrationStatus >= 1 || userData.registrationStatus === null
    // Only show dialog if registration activation is needed
    if (showDialog) {
      // Small delay to ensure page is fully rendered
      setTimeout(() => {
        showSsoDialog.value = true
        // Mark as shown in sessionStorage
        sessionStorage.setItem(ssoDialogShownKey, 'true')
        // Clear the SSO login flag so it doesn't show again on page refresh
        sessionStorage.removeItem('sso_login_completed')
      }, 500)
    } else {
      // Registration activation is not needed, clear the SSO flag
      sessionStorage.removeItem('sso_login_completed')
    }
  } catch (error) {
    console.error('App: Error fetching registration status:', error)
    sessionStorage.removeItem('sso_login_completed')
  }
}

// Watch for route changes and store initialization to check for SSO dialog
watch([() => route.path, () => store.isLoaded], () => {
  if (store.isLoaded && !isLandingPage()) {
    checkAndShowSsoDialog()
  }
})

const closeSsoDialog = () => {
  showSsoDialog.value = false
}

// Handle POST request when link is clicked
const handlePostRequest = async () => {
  API.Post.resendActivationLink().then(results => {
    if (results?.success) {
    }
  })
  showSsoDialog.value = false
}

/**
 * Note: SSO payload handling is now done by router redirects and landing pages
 * Note: Store initialization is handled by the initialize middleware for protected routes
 */
onMounted(async () => {
  // Set locale if needed
  if (i18n.locale.value !== sessionStore.getLocaleCode()) {
    i18n.locale.value = sessionStore.getLocaleCode()
  }
  // If we're already on a landing page, don't do any redirects
  if (isLandingPage()) {
    console.log('App: Already on a landing page, skipping redirects')
    return
  }

  // App is ready
  initialLoginCheckProcessing.value = false
})
</script>

<template>
  <header>
    <Toast position="bottom-right" />
    <nav
      v-if="
        !initialLoginCheckProcessing &&
        !isRouteTransitioning &&
        storeInitialized &&
        !isChromelessPage()
      "
      :key="storeInitialized ? 'initialized' : 'loading'"
    >
      <MobileNav v-if="isMobile" />
      <DesktopNav v-else />
    </nav>
  </header>
  <div
    v-if="!initialLoginCheckProcessing && !isRouteTransitioning"
    class="PageContainer"
    :class="{ 'PageContainer--chromeless': isChromelessPage() }"
  >
    <RouterView v-slot="{ Component }">
      <component :is="Component" />
    </RouterView>
  </div>
  <div v-else>
    <FullScreenLoading />
  </div>

  <!-- Activation Email Dialog - Shows on any page after SSO login -->
  <Dialog
    v-model:visible="showSsoDialog"
    modal
    :style="{ width: '90vw', maxWidth: '500px' }"
    :closable="true"
    @update:visible="showSsoDialog = $event"
  >
    <template #header>
      <h3 class="m-0">Email activation</h3>
    </template>
    <div>
      <div class="mb-5" style="color: red">
        Your email has not been confirmed! Your account will expire on {{ userData?.expireTime }}!
      </div>
      <a
        href="#"
        @click.prevent="handlePostRequest"
        class="text-primary cursor-pointer hover:underline"
      >
        Resend activation information
      </a>
    </div>
    <template #footer>
      <Button label="Cancel" text @click="closeSsoDialog" />
    </template>
  </Dialog>
</template>

<style scoped>
.PageContainer {
  padding-top: 60px; /* Match nav height */
}

/* Chromeless pages (standalone / public enrollment embed) render without the
   top nav, so they must not reserve space for it. */
.PageContainer--chromeless {
  padding-top: 0 !important;
}

@media (min-width: 40rem) {
  /* sm - 640px */
  .PageContainer {
    padding-top: 62px; /* Match nav height */
  }
}

@media (min-width: 48rem) {
  /* md - 768px */
  .PageContainer {
    padding-top: 74px; /* Match nav height */
  }
}

@media (min-width: 64rem) {
  /* lg - 1024px */
}

@media (min-width: 75rem) {
  /* xl - 1200px */
}

@media (min-width: 80rem) {
  /* 2xl - 1280px */
}
</style>
